import type { GeoLocation, PurposeEnum } from '@bambi/api-client/schemas';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { FormFieldContainer } from '@bambi/component-library/src/components/forms/layout/FormFieldContainer';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

import { GeolocationPicker } from './GeolocationPicker';
import { useFeature } from 'src/app/FeatureToggle';
import type { FormFieldControllerRulesProps } from 'src/common/FormField/FormFieldController';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import type { LocationTypeaheadProps } from 'src/common/LocationTypeahead/LocationTypeahead';
import { LocationTypeahead } from 'src/common/LocationTypeahead/LocationTypeahead';

/**
 * A form-controlled location (pickup) address field.
 *
 * The goal is to provide a single, flexible component that can be re-used in
 * both contexts (and any future ones) by driving behavior through props
 * rather than copy-pasting slightly different implementations.
 */
export interface FormControlledTripAddressFieldProps {
  /**
   * The fully-qualified field name for react-hook-form. E.g.
   *  – "trips.0.pickupLocation.address"
   *  – "pickup.address"
   */
  name: string;

  /**
   * Label shown above the input. Defaults to "Pickup Address *".
   */
  label: string;

  /**
   * Current value of the combobox. If omitted, the value managed by
   * react-hook-form will be used.
   */
  value?: SelectOption;

  /**
   * The purpose of the address field.
   */
  purpose: PurposeEnum;

  /**
   * Callback that is executed whenever a location is selected. When not
   * provided, the component will update the form value with the selected
   * option (label/value).
   *
   * The callback receives the selected location option as its first argument
   * and the react-hook-form field object as the second.
   */
  onLocationChange?: (location: SelectOption, field: any) => void;

  /** Forwarded to LocationTypeahead */
  additionalResultsQueryFn?: LocationTypeaheadProps['additionalResultsQueryFn'];
  /** Forwarded to LocationTypeahead */
  renderOption?: LocationTypeaheadProps['renderOption'];
  /** Forwarded to LocationTypeahead */
  disabled?: boolean;

  /**
   * Custom validation rules – falls back to ensuring that a suggestion with a
   * `value` has been selected.
   */
  rules?: FormFieldControllerRulesProps;
}

export function FormControlledTripAddressField({
  name,
  label,
  value,
  purpose,
  onLocationChange,
  additionalResultsQueryFn,
  renderOption,
  disabled,
  rules,
}: FormControlledTripAddressFieldProps) {
  const { isEnabled: isGeolocationsEnabled } = useFeature('geolocations');

  return (
    <FormFieldController
      name={name}
      rules={
        rules ?? {
          validate(location) {
            if (!location || !location.value) {
              return `A ${purpose} location from suggestions is required`;
            }
            return true;
          },
        }
      }
    >
      {({ field, fieldState }) => {
        const inputProps = {
          id: name,
          ...field,
        } as const;

        const handleLocationChange = (selectedOption: SelectOption) => {
          if (onLocationChange) {
            onLocationChange(selectedOption, field);
          } else {
            // Default behavior – update the form field value.
            field.onChange({
              value: selectedOption.value,
              label: selectedOption.label,
            });
          }
        };

        const handleGeolocationSelect = (geolocation: GeoLocation) => {
          handleLocationChange({
            value: geolocation.location.place_id,
            label: geolocation.label,
          });
        };

        return (
          <FormFieldContainer>
            <DefaultFormFieldLayout
              label={label}
              error={fieldState.error?.message?.toString()}
              inputId={inputProps.id}
            >
              <div className="flex flex-row gap-2">
                {/* Wrap LocationTypeahead in relative container to isolate
                dropdown positioning from parent flex layout */}
                <div className="relative flex-1">
                  <LocationTypeahead
                    inputProps={inputProps}
                    error={fieldState.error?.message?.toString()}
                    onChange={handleLocationChange}
                    // Prefer the externally-controlled value, otherwise fall back to
                    // react-hook-form's internal value.
                    value={value ?? field.value}
                    additionalResultsQueryFn={additionalResultsQueryFn}
                    renderOption={renderOption}
                    disabled={disabled}
                    data-testid={`address-input-${name}`}
                  />
                </div>
                {isGeolocationsEnabled && (
                  <GeolocationPicker onSelect={handleGeolocationSelect} />
                )}
              </div>
            </DefaultFormFieldLayout>
          </FormFieldContainer>
        );
      }}
    </FormFieldController>
  );
}
